// Product Page Combined Script - Optimiert für Performance
// Enthält: Slider, Countdown, Sticky CTA, FAQ

// ==========================================
// SLIDER COMPONENT (Optimized with Event Delegation)
// ==========================================
class ProductSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('#sliderTrack');
    this.slides = container.querySelectorAll('.slider-slide');
    this.prevBtn = container.querySelector('.prev');
    this.nextBtn = container.querySelector('.next');
    this.currentSlideSpan = document.getElementById('currentSlide');
    this.totalSlidesSpan = document.getElementById('totalSlides');
    
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;
    this.slideWidth = 0;
    
    this.init();
  }

  init() {
    this.totalSlidesSpan.textContent = this.totalSlides;
    
    this.prevBtn.addEventListener('click', () => this.prevSlide());
    this.nextBtn.addEventListener('click', () => this.nextSlide());
    
    // Event Delegation für Touch/Mouse Events auf dem Track statt auf jedem Slide
    this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.touchEnd.bind(this));
    this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: true });
    
    this.track.addEventListener('mousedown', this.touchStart.bind(this));
    this.track.addEventListener('mouseup', this.touchEnd.bind(this));
    this.track.addEventListener('mouseleave', this.touchEnd.bind(this));
    this.track.addEventListener('mousemove', this.touchMove.bind(this));
    
    this.track.addEventListener('contextmenu', e => e.preventDefault());
    
    // Bilder optimieren
    this.slides.forEach((slide, index) => {
      const img = slide.querySelector('img');
      if (img) {
        img.draggable = false;
        if (index < 3 && 'decode' in img) {
          img.decode().catch(() => {});
        }
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prevSlide();
      if (e.key === 'ArrowRight') this.nextSlide();
    });

    this.updateDimensions();
    
    // Use ResizeObserver to avoid forced reflow on window resize events
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        // Read width from the entry, avoiding layout thrashing
        this.slideWidth = entry.contentRect.width;
        this.setPositionByIndex();
      }
    });
    resizeObserver.observe(this.container);

    this.preloadAdjacentImages();
  }

  updateDimensions() {
    // Fallback for initial load
    this.slideWidth = this.container.clientWidth;
    this.setPositionByIndex();
  }

  touchStart(event) {
    // Nur starten wenn auf einem Slide
    if (!event.target.closest('.slider-slide')) return;

    this.isDragging = true;
    this.startPos = this.getPositionX(event);
    this.animationID = requestAnimationFrame(this.animation.bind(this));
    this.container.classList.add('dragging');
  }

  touchMove(event) {
    if (this.isDragging) {
      const currentPosition = this.getPositionX(event);
      this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
    }
  }

  touchEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.container.classList.remove('dragging');
    
    const movedBy = this.currentTranslate - this.prevTranslate;
    
    if (movedBy < -50 && this.currentIndex < this.totalSlides - 1) {
      this.currentIndex += 1;
    }
    
    if (movedBy > 50 && this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
    
    this.setPositionByIndex();
    this.updateUI();
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  animation() {
    this.setSliderPosition();
    if (this.isDragging) {
      requestAnimationFrame(this.animation.bind(this));
    }
  }

  setSliderPosition() {
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  setPositionByIndex() {
    this.currentTranslate = this.currentIndex * -this.slideWidth;
    this.prevTranslate = this.currentTranslate;
    this.setSliderPosition();
  }

  nextSlide() {
    if (this.currentIndex < this.totalSlides - 1) {
      this.currentIndex++;
      this.setPositionByIndex();
      this.updateUI();
      this.preloadAdjacentImages();
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.setPositionByIndex();
      this.updateUI();
      this.preloadAdjacentImages();
    }
  }

  updateUI() {
    this.currentSlideSpan.textContent = this.currentIndex + 1;
    this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.5' : '1';
    this.nextBtn.style.opacity = this.currentIndex === this.totalSlides - 1 ? '0.5' : '1';
  }

  preloadAdjacentImages() {
    const preloadIndexes = [
      this.currentIndex - 1,
      this.currentIndex + 1
    ].filter(i => i >= 3 && i < this.totalSlides);

    preloadIndexes.forEach(index => {
      const img = this.slides[index]?.querySelector('img');
      if (img && img.loading === 'lazy' && !img.complete) {
        const preloadImg = new Image();
        preloadImg.srcset = img.srcset;
        preloadImg.sizes = img.sizes;
        preloadImg.src = img.src;
      }
    });
  }
}

// ==========================================
// COUNTDOWN TIMER (Optimized with IntersectionObserver)
// ==========================================
function initCountdown() {
  // Check if we have the new timer structure
  const dayEl = document.getElementById('d');
  const hourEl = document.getElementById('h');
  const minEl = document.getElementById('m');
  const secEl = document.getElementById('s');
  
  if (!dayEl || !hourEl || !minEl || !secEl) return;
  
  let rafId;
  let lastUpdate = 0;
  let isVisible = false;
  
  // Target Date: Nov 28, 2025 23:59:59
  const countDownDate = new Date("Nov 28, 2025 23:59:59").getTime();
  
  function updateCountdown(timestamp) {
    if (!isVisible) return;

    if (timestamp - lastUpdate < 1000) {
      rafId = requestAnimationFrame(updateCountdown);
      return;
    }
    lastUpdate = timestamp;
    
    const now = new Date().getTime();
    const distance = countDownDate - now;
    
    if (distance < 0) {
      dayEl.innerText = "0";
      hourEl.innerText = "0";
      minEl.innerText = "0";
      secEl.innerText = "0";
      return;
    }
    
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    
    dayEl.innerText = days;
    hourEl.innerText = hours;
    minEl.innerText = minutes;
    secEl.innerText = seconds;
    
    rafId = requestAnimationFrame(updateCountdown);
  }
  
  const timerContainer = document.querySelector('.timer-container');
  if (!timerContainer) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      isVisible = entry.isIntersecting;
      if (isVisible) {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(updateCountdown);
      } else {
        if (rafId) cancelAnimationFrame(rafId);
      }
    });
  }, { threshold: 0 });

  observer.observe(timerContainer);
}

// ==========================================
// FUNNEL MODAL
// ==========================================
function initFunnelModal() {
  const modal = document.getElementById('funnel-modal');
  const closeBtn = document.querySelector('.modal-close');
  const openButtons = document.querySelectorAll('.js-open-funnel');
  let lastFocusedElement;

  function openModal(e) {
    if (e) e.preventDefault();
    lastFocusedElement = document.activeElement;
    if (modal) {
      modal.classList.add('show');
      modal.setAttribute('aria-hidden', 'false');
    }
    document.body.classList.add('modal-open');
    
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    const trustedShopsFlyIn = document.querySelector('._uuhri8');
    if (trustedShopsFlyIn) trustedShopsFlyIn.style.display = 'none';
    
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (modal) {
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('modal-open');
    
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
    
    const trustedShopsFlyIn = document.querySelector('._uuhri8');
    if (trustedShopsFlyIn) trustedShopsFlyIn.style.display = '';
    
    if (lastFocusedElement) lastFocusedElement.focus();
  }

  openButtons.forEach(btn => btn.addEventListener('click', openModal));
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (modal) {
    modal.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
    modal.addEventListener('click', e => { if (e.target === modal) closeModal(); });
  }
}

// ==========================================
// FAQ ACCORDION (Optimized with Event Delegation)
// ==========================================
function initFAQ() {
  const accordion = document.getElementById('accordion');
  if (!accordion) return;

  function closeAllPanels() {
    const openItems = accordion.querySelectorAll('.accordion-item.open');
    openItems.forEach(item => {
      item.classList.remove('open');
      const panel = item.querySelector('.accordion-panel');
      if(panel) panel.style.maxHeight = null;
    });
  }
  
  function openPanel(item, panel) {
    item.classList.add('open');
    requestAnimationFrame(() => {
      panel.style.maxHeight = panel.scrollHeight + 'px';
    });
  }
  
  // Event Delegation: Ein Listener für alle Header
  accordion.addEventListener('click', function(e) {
    const header = e.target.closest('.accordion-header');
    if (!header) return;

    const item = header.parentElement;
    const panel = header.nextElementSibling;
    const isOpen = item.classList.contains('open');
    
    closeAllPanels();
    
    if (!isOpen) {
      openPanel(item, panel);
    }
  }, { passive: true });
}

// ==========================================
// INIT ALL COMPONENTS (Lazy Load Non-Critical)
// ==========================================
document.addEventListener('DOMContentLoaded', function() {
  // Critical: Slider (sofort initialisieren)
  const sliderContainer = document.getElementById('productSlider');
  if (sliderContainer) {
    new ProductSlider(sliderContainer);
  }
  
  // Non-Critical: Verzögert initialisieren um Main Thread zu entlasten
  const initLazyComponents = () => {
    initCountdown();
    initFunnelModal();
    initFAQ();
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(initLazyComponents);
  } else {
    setTimeout(initLazyComponents, 200);
  }
});
